/**
 * Challenge Auto-Card — for challenge_joined and challenge_completed posts.
 * Premium card with cover, stats, link. Minimalist.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Trophy, Users, ChevronRight, Award } from 'lucide-react'

interface ChallengeAutoCardProps {
  postType: 'challenge_joined' | 'challenge_completed'
  challengeId: string | null
  metadata: {
    challenge_name?: unknown
    cover_emoji?: unknown
    cover_gradient?: unknown
    members_count?: unknown
    rank?: unknown
    total_members?: unknown
    completion_rate?: unknown
  }
}

export const ChallengeAutoCard = memo(function ChallengeAutoCard({
  postType, challengeId, metadata,
}: ChallengeAutoCardProps): React.ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isCompleted = postType === 'challenge_completed'

  const name = String(metadata.challenge_name ?? t('feed.auto.unknownChallenge'))
  const emoji = String(metadata.cover_emoji ?? '🏆')
  const gradient = String(metadata.cover_gradient ?? '')
  const rate = Number(metadata.completion_rate ?? 0)
  const rank = Number(metadata.rank ?? 0)
  const total = Number(metadata.total_members ?? 0)
  const members = Number(metadata.members_count ?? 0)

  const bgStyle = gradient
    ? { background: gradient }
    : { background: 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(34,197,94,0.25))' }

  return (
    <div className="rounded-2xl overflow-hidden border border-white/[0.05]">
      {/* Cover */}
      <div className="relative h-[72px] flex items-center gap-3 px-4" style={bgStyle}>
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
        <span className="relative text-2xl">{emoji}</span>
        <div className="relative flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-white truncate">{name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {isCompleted ? (
              <Award size={11} className="text-white/60" />
            ) : (
              <Trophy size={11} className="text-white/60" />
            )}
            <span className="text-[11px] text-white/60 font-medium">
              {isCompleted ? t('feed.auto.completed') : t('feed.auto.joined')}
            </span>
          </div>
        </div>
      </div>

      {/* Stats footer */}
      <div className="px-4 py-3 bg-white/[0.015] flex items-center justify-between">
        <div className="flex items-center gap-4 text-[11px] text-text-hint/50">
          {isCompleted && rank > 0 && (
            <span className="font-medium text-text-hint">#{rank} / {total}</span>
          )}
          {isCompleted && rate > 0 && (
            <span className="text-success font-semibold">{rate}%</span>
          )}
          {!isCompleted && members > 0 && (
            <span className="flex items-center gap-1">
              <Users size={10} />
              {members.toLocaleString()}
            </span>
          )}
        </div>

        {challengeId && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/challenges/${challengeId}`) }}
            className="flex items-center gap-0.5 text-[11px] text-primary/70 font-medium hover:text-primary tap-highlight-transparent"
          >
            {t('feed.auto.viewChallenge')}
            <ChevronRight size={13} />
          </button>
        )}
      </div>
    </div>
  )
})
